import {
  Modal,
  IconButton,
  Button,
  ButtonSize,
  ButtonVariant,
  Input,
  InputSize,
  IconButtonVariant,
  BaseInput
} from "shared/components";
import { FC, useEffect, useMemo, useState, useCallback, useRef } from "react";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";

import {
  Cancel01Icon,
  InformationCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { Table } from "../Table";
import {
  CellContext,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { ApplicationFeeStatus, FeeStatusItem } from "shared/types";
import { Applicant } from "types/application";
import { getFullName } from "shared/utils/get-full-name";
import moment from "moment/moment";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { Money } from "types/landlord/reports";
import { Loader } from "shared/components/Loader";
import { logger } from "logger";
import type { IconSvgElement } from "@hugeicons/react";
import { moneyCentsParser } from "shared/components/forms/BaseInput/parsers";
import { cx, has } from "shared/utils";

interface Props {
  onClose: () => void;
  applicationId: string;
  onSuccess?: () => void;
  applicantId?: string;
  queryKey?: unknown[];
}

interface FeeRefund {
  fee_type: string;
  refund_amount: Money;
}

interface RefundAmount {
  applicant_id: string | number;
  fee_refunds?: FeeRefund[];
  refund_amount?: Money;
}

interface ApplicantWithRefundAmountInput extends Applicant {
  refundAmountInput: number;
}

// Per-fee refund amounts keyed by `${applicantId}:${feeType}` → cents
type FeeRefundAmounts = Record<string, number>;

const useApplicationMeta = (applicationId: string) => {
  return useQuery({
    queryKey: ["application", applicationId],
    queryFn: () =>
      instance(`/portal/v1/applications/${applicationId}`).then(
        ({ data }) => data
      ),
    enabled: Boolean(applicationId)
  });
};

const useRefundMutation = (
  applicationId: string,
  applicantId?: string,
  invalidateKeys?: unknown[],
  onClose?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      refundData?:
        | { applicant_refunds: RefundAmount[] }
        | { refund_amount: Money }
        | { fee_refunds: FeeRefund[] }
    ) => {
      return instance.post(
        applicantId
          ? `/portal/v1/applications/${applicationId}/applicants/${applicantId}/refund`
          : `/portal/v1/applications/${applicationId}/refund`,
        refundData
      );
    },
    onSuccess: () => {
      toast.success("Application successfully refunded");
      if (invalidateKeys?.length)
        queryClient
          .invalidateQueries({ queryKey: invalidateKeys })
          .catch((err) => {
            logger.error("Failed to invalidate queries", err);
          });
      onClose?.();
    },
    onError: (error) => {
      toast.error(`Error refunding applications: ${getErrorMessage(error)}`);
    }
  });
};

const feeKey = (applicantId: string, feeType: string) =>
  `${applicantId}:${feeType}`;

export const RefundModal: FC<Props> = ({
  onClose,
  applicationId,
  applicantId,
  queryKey
}) => {
  const applicationMeta = useApplicationMeta(applicationId);
  const { mutate: refund, isPending } = useRefundMutation(
    applicationId,
    applicantId,
    queryKey,
    onClose
  );

  const paidApplicants = useMemo(
    () =>
      (applicationMeta.data?.applicants || []).filter(
        (applicant: Applicant) =>
          applicant.application_fee_status === ApplicationFeeStatus.Paid
      ),
    [applicationMeta.data]
  );

  const applicant = useMemo(
    () =>
      paidApplicants.find(
        (applicant: Applicant) =>
          applicantId && String(applicant.id) === String(applicantId)
      ),
    [paidApplicants, applicantId]
  );

  const relevantApplicants = useMemo(
    () => (applicant ? [applicant] : paidApplicants),
    [applicant, paidApplicants]
  );

  const hasPerFeeData = useMemo(
    () =>
      relevantApplicants.some(
        (a: Applicant) =>
          a.fee_statuses && a.fee_statuses.some((fs) => fs.status === "paid")
      ),
    [relevantApplicants]
  );

  // Collect unique paid fee types across all relevant applicants (for column headers)
  const paidFeeTypes = useMemo(() => {
    const feeTypeSet = new Map<string, string>();
    for (const a of relevantApplicants)
      for (const fs of a.fee_statuses || [])
        if (fs.status === "paid" && !feeTypeSet.has(fs.fee_type))
          feeTypeSet.set(fs.fee_type, fs.description);

    return Array.from(feeTypeSet.entries()).map(([fee_type, description]) => ({
      fee_type,
      description
    }));
  }, [relevantApplicants]);

  // Per-fee refund amounts: `${applicantId}:${feeType}` → cents
  const [feeRefundAmounts, setFeeRefundAmounts] = useState<FeeRefundAmounts>(
    {}
  );
  const feeRefundAmountsRef = useRef<FeeRefundAmounts>({});

  // Initialize per-fee amounts when data loads
  const feeRefundInitialized = useRef(false);
  useEffect(() => {
    if (!hasPerFeeData || feeRefundInitialized.current) return;
    feeRefundInitialized.current = true;

    const initial: FeeRefundAmounts = {};
    for (const a of relevantApplicants)
      for (const fs of a.fee_statuses || [])
        if (fs.status === "paid")
          initial[feeKey(String(a.id), fs.fee_type)] = fs.amount.cents;

    feeRefundAmountsRef.current = initial;
    setFeeRefundAmounts(initial);
  }, [hasPerFeeData, relevantApplicants]);

  // Get max (paid) amount for a specific fee of a specific applicant
  const getMaxCents = useCallback(
    (applicantId: string, feeType: string): number => {
      const a = relevantApplicants.find((a) => String(a.id) === applicantId);
      const fs = a?.fee_statuses?.find(
        (fs: FeeStatusItem) => fs.fee_type === feeType && fs.status === "paid"
      );
      return fs?.amount.cents || 0;
    },
    [relevantApplicants]
  );

  const handleFeeAmountChange = useCallback(
    (applicantId: string, feeType: string, cents: number | null) => {
      if (!cents) cents = 0;
      const key = feeKey(applicantId, feeType);
      feeRefundAmountsRef.current = {
        ...feeRefundAmountsRef.current,
        [key]: cents
      };
      setFeeRefundAmounts((prev) => ({ ...prev, [key]: cents }));
    },
    []
  );

  // ── Legacy single-amount state (fallback when no per-fee data) ──
  const [refundAmounts, setRefundAmounts] = useState<Record<string, number>>(
    {}
  );

  const baseTableData = useMemo(
    () =>
      relevantApplicants.map((applicant) => ({
        ...applicant,
        refundAmountInput: applicant.refundable_invoice?.total?.cents || 0
      })),
    [relevantApplicants]
  );

  // Initialize legacy refund amounts when base data changes
  useEffect(() => {
    const initial = baseTableData.reduce(
      (acc, applicant) => {
        acc[String(applicant.id)] = applicant.refundAmountInput;
        return acc;
      },
      {} as Record<string, number>
    );
    setRefundAmounts(initial);
  }, [baseTableData]);

  const tableData = useMemo(() => {
    return baseTableData.map((applicant) => ({
      ...applicant,
      refundAmountInput: refundAmounts[String(applicant.id)]
    }));
  }, [baseTableData, refundAmounts]);

  const handleAmountChange = useCallback(
    (applicantId: string, value: string) => {
      const dollarValue = parseFloat(value) || 0;
      const centsValue = Math.round(dollarValue * 100);
      setRefundAmounts((prev) => ({
        ...prev,
        [applicantId]: centsValue
      }));
    },
    []
  );

  const canRefund = useMemo(() => {
    if (hasPerFeeData)
      // At least one fee must have amount > 0, and none can exceed max
      return relevantApplicants.every((a) => {
        const id = String(a.id);
        const paidFees = (a.fee_statuses || []).filter(
          (fs: FeeStatusItem) => fs.status === "paid"
        );
        const hasAtLeastOne = paidFees.some((fs: FeeStatusItem) => {
          const cents = feeRefundAmounts[feeKey(id, fs.fee_type)] || 0;
          return cents > 0;
        });
        const allValid = paidFees.every((fs: FeeStatusItem) => {
          const cents = feeRefundAmounts[feeKey(id, fs.fee_type)] || 0;
          return cents >= 0 && cents <= fs.amount.cents;
        });
        return hasAtLeastOne && allValid;
      });

    return tableData.every((applicant) => {
      const applicantId = String(applicant.id);
      const amountCents = refundAmounts[applicantId] || 0;
      const maxAmountCents = applicant.refundable_invoice?.total?.cents || 0;
      return amountCents > 0 && amountCents <= maxAmountCents;
    });
  }, [
    hasPerFeeData,
    relevantApplicants,
    feeRefundAmounts,
    tableData,
    refundAmounts
  ]);

  const buildFeeRefunds = useCallback(
    (a: Applicant): FeeRefund[] => {
      const id = String(a.id);
      const paidFees = (a.fee_statuses || []).filter(
        (fs: FeeStatusItem) => fs.status === "paid"
      );
      const result: FeeRefund[] = [];
      for (const fs of paidFees) {
        const cents = feeRefundAmounts[feeKey(id, fs.fee_type)] || 0;
        if (cents > 0)
          result.push({
            fee_type: fs.fee_type,
            refund_amount: { cents, currency: "USD" }
          });
      }
      return result;
    },
    [feeRefundAmounts]
  );

  const handleRefund = useCallback(() => {
    if (hasPerFeeData) {
      if (applicantId) {
        // Single-applicant endpoint: send fee_refunds directly
        const a = relevantApplicants[0];
        refund({ fee_refunds: buildFeeRefunds(a) });
      } else {
        // Bulk endpoint: wrap in applicant_refunds
        const applicant_refunds: RefundAmount[] = relevantApplicants.map(
          (a) => ({ applicant_id: a.id, fee_refunds: buildFeeRefunds(a) })
        );
        refund({ applicant_refunds });
      }
      return;
    }

    if (applicantId) {
      refund({
        refund_amount: {
          cents: refundAmounts[applicantId],
          currency: "USD"
        }
      });
    } else {
      const applicant_refunds = Object.entries(refundAmounts).map(
        ([applicantId, amount]) => ({
          applicant_id: applicantId,
          refund_amount: { cents: amount, currency: "USD" }
        })
      );
      refund({ applicant_refunds });
    }
  }, [
    hasPerFeeData,
    relevantApplicants,
    buildFeeRefunds,
    applicantId,
    refund,
    refundAmounts
  ]);

  const perFeeColumns = useMemo(() => {
    const cols = [
      {
        id: "applicant_name",
        accessorFn: (data: ApplicantWithRefundAmountInput) =>
          getFullName({
            firstName: data.first_name,
            lastName: data.last_name,
            middleName: data.middle_name,
            short: true
          }),
        header: "Applicant",
        size: 120,
        cell: (data: CellContext<ApplicantWithRefundAmountInput, string>) => (
          <p className="text-sm truncate text-content-1000 font-medium">
            {data.getValue()}
          </p>
        )
      },
      {
        accessorKey: "refundable_invoice.created_at",
        header: "Charge date",
        size: 130,
        cell: (data: CellContext<ApplicantWithRefundAmountInput, string>) => (
          <p className="text-sm text-content-400">
            {data.getValue() && moment(data.getValue()).isValid()
              ? moment(data.getValue()).format("MMM DD, YYYY")
              : "--"}
          </p>
        )
      }
    ];

    for (const { fee_type, description } of paidFeeTypes)
      cols.push({
        accessorKey: `refundable_invoice.${fee_type}`,
        header: description,
        size: 160,
        cell: (data: CellContext<ApplicantWithRefundAmountInput, string>) => {
          const applicantRow = data.row.original;
          const id = String(applicantRow.id);
          const isFeeApplicable =
            applicantRow.fee_statuses?.find((fee) => fee.fee_type === fee_type)
              ?.status !== "not_applicable";
          if (!isFeeApplicable) return <div aria-hidden />;

          const key = feeKey(id, fee_type);
          const maxCents = getMaxCents(id, fee_type);
          const currentCents = has(feeRefundAmountsRef.current[key])
            ? feeRefundAmountsRef.current[key]
            : maxCents;

          let error = "";
          if (currentCents < 0) error = "Invalid";
          else if (currentCents > maxCents) error = "Exceeds max";

          return (
            <div className="flex items-center gap-1.5">
              <BaseInput
                className={cx("max-w-28", error ? "!mb-0.5" : "!mb-0")}
                parser={moneyCentsParser({ max: maxCents })}
                id={`refund-${id}-${fee_type}`}
                prefix="$"
                value={currentCents}
                onChange={(cents: number | null) =>
                  handleFeeAmountChange(id, fee_type, cents)
                }
                placeholder="0.00"
                hideOptional
                error={error}
                ErrorIcon={InformationCircleIcon}
                size={InputSize.SM}
              />
              <span className="text-sm text-content-400 whitespace-nowrap">
                / ${(maxCents / 100).toFixed(2)}
              </span>
            </div>
          );
        }
      });

    return cols;
  }, [paidFeeTypes, getMaxCents, handleFeeAmountChange]);

  const legacyColumns = useMemo(() => {
    return [
      {
        accessorFn: (data: ApplicantWithRefundAmountInput) =>
          getFullName({
            firstName: data.first_name,
            lastName: data.last_name,
            middleName: data.middle_name,
            short: true
          }),
        header: "Applicant",
        cell: (data: CellContext<ApplicantWithRefundAmountInput, string>) => (
          <p className="text-sm truncate text-content-1000 font-medium">
            {data.getValue()}
          </p>
        )
      },
      {
        accessorKey: "refundable_invoice.created_at",
        header: "Charge date",
        size: 120,
        cell: (data: CellContext<ApplicantWithRefundAmountInput, string>) => (
          <p className="text-sm text-content-400">
            {data.getValue() && moment(data.getValue()).isValid()
              ? moment(data.getValue()).format("MMM DD, YYYY")
              : "--"}
          </p>
        )
      },
      {
        accessorKey: "refundable_invoice.total",
        header: "Amount",
        size: 120,
        cell: (data: CellContext<ApplicantWithRefundAmountInput, Money>) => {
          const applicant = data.row.original;
          const applicantId = String(applicant.id);
          const maxAmountCents = data.getValue().cents || 0;
          const refundAmountInput = applicant.refundAmountInput || 0;

          let error = "";
          if (!refundAmountInput || refundAmountInput <= 0)
            error = "This field is required";
          else if (refundAmountInput > maxAmountCents) error = "Invalid amount";

          return (
            <Input
              id={`refund-amount-${applicantId}`}
              type="money"
              className={error ? "!mb-0.5" : "!mb-0"}
              defaultValue={String(maxAmountCents / 100)}
              onChange={(e) => handleAmountChange(applicantId, e.target.value)}
              placeholder="0.00"
              hideOptional
              noIntlNormalization
              error={error}
              HugeErrorIcon={InformationCircleIcon as IconSvgElement}
              size={InputSize.SM}
            />
          );
        }
      }
    ];
  }, [handleAmountChange]);

  const columns = hasPerFeeData ? perFeeColumns : legacyColumns;

  const table = useReactTable<ApplicantWithRefundAmountInput>({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id) // Stable row keys
  });

  return (
    <Modal
      opened={Boolean(applicationId)}
      onClose={onClose}
      panelClassName={`${hasPerFeeData ? "max-w-[900px]" : "max-w-[600px]"} w-full flex flex-col`}
    >
      <div className="p-6 border-b border-alpha-100 flex items-center h-20 justify-between">
        <h1 className="text-xl text-content-1000 font-bold ">Confirm refund</h1>
        <IconButton
          onClick={onClose}
          className="ml-auto"
          HugeIcon={Cancel01Icon}
          variant={IconButtonVariant.TEXT_BASE}
        />
      </div>
      <div className="bg-warning-50 grow-0 px-6 py-4 flex gap-4 items-center border-b border-alpha-100">
        <InfoIcon className="text-warning-500 h-5 w-5" />
        <p className="text-sm text-warning-700 font-medium">
          You cannot undo this action. You can only refund a customer one time,
          partially or fully. Once a refund is issued it can take up to 5-7
          business days.
        </p>
      </div>
      <div className="h-full grow-1 overflow-x-auto overflow-y-auto px-6 py-4 bg-background-900">
        {applicationMeta.isLoading ? (
          <div className="w-full flex items-center justify-center h-full">
            <Loader />
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-content-500 mb-2.5">
              These items will be refunded
            </p>
            <Table
              table={table}
              rowHeight="68px"
              thClassName="px-3"
              tdClassName="px-3"
            />
          </>
        )}
      </div>
      <div className="flex gap-2 h-[72px] justify-end px-6 py-4 border-t border-alpha-100">
        <Button
          className="w-[103px]"
          variant={ButtonVariant.BASE}
          onClick={() => onClose()}
          size={ButtonSize.SM}
        >
          Cancel
        </Button>
        <Button
          className="w-[103px]"
          variant={ButtonVariant.PRIMARY}
          onClick={handleRefund}
          loading={isPending}
          size={ButtonSize.SM}
          disabled={applicationMeta.isLoading || isPending || !canRefund}
        >
          Refund
        </Button>
      </div>
    </Modal>
  );
};
